import { useMsal } from "@azure/msal-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  deleteAdminReportJob,
  downloadAdminReport,
  getReportCapacity,
  listAdminReportJobs,
  resendAdminReportEmails,
  stopAdminReportJob,
  triggerBrowserDownload,
} from "../services/pdfService";
import { useI18n } from "../i18n";
import ClientThumb from "./ClientThumb";
import "./AdminExecutionsPage.css";

const POLL_MS = 5000;

function formatIsoDate(value) {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return date.toLocaleString("fr-FR");
}

function formatCompactDate(value) {
  if (!value) {
    return "";
  }
  const asNum = Number(value);
  const date = Number.isFinite(asNum) ? new Date(asNum) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
}

function formatDateRange(startDate, endDate) {
  const start = formatCompactDate(startDate);
  const end = formatCompactDate(endDate);
  if (!start && !end) {
    return "-";
  }
  if (start && end) {
    return `${start} → ${end}`;
  }
  return start || end;
}

function getUniqueValues(items, key) {
  return Array.from(
    new Set(items.map((item) => item[key]).filter(Boolean))
  ).sort((a, b) => String(a).localeCompare(String(b), "fr"));
}

function getStatusClassName(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "completed") return "status-badge status-completed";
  if (normalized === "completed_with_warnings") return "status-badge status-warning";
  if (normalized === "failed") return "status-badge status-failed";
  if (normalized === "cancelled") return "status-badge status-cancelled";
  if (normalized === "cancelled_by_admin") return "status-badge status-cancelled-by-admin";
  if (normalized === "expired") return "status-badge status-expired";
  if (normalized === "queued") return "status-badge status-queued";
  if (normalized === "running") return "status-badge status-running";
  return "status-badge";
}

function getJobWarningMessage(job) {
  if (!job || job.status !== "completed_with_warnings") {
    return "";
  }
  return String(job.message || job.error?.message || "").trim();
}

function getEmailResults(job) {
  return Array.isArray(job?.emailResults) ? job.emailResults : [];
}

function isRecipientSent(result) {
  // "accepted" = taken by Graph; "delivered" = no bounce within the
  // verification window; "success" kept for backward compatibility.
  const status = String(result?.status || "").toLowerCase();
  return status === "accepted" || status === "success" || status === "delivered";
}

function isRecipientDelivered(result) {
  return String(result?.status || "").toLowerCase() === "delivered";
}

function isRecipientBounced(result) {
  return String(result?.status || "").toLowerCase() === "bounced";
}

function isRecipientFailed(result) {
  const status = String(result?.status || "").toLowerCase();
  return status === "failed" || status === "bounced";
}

// Recipients that failed or bounced — used to target a resend.
function getFailedRecipients(job) {
  return getEmailResults(job)
    .filter((r) => isRecipientFailed(r))
    .map((r) => r.recipient)
    .filter(Boolean);
}

// Failed/expired jobs can still be resent when their PDF was generated before
// the failure (e.g. mail token error); the back answers 410 otherwise.
const RESENDABLE_STATUSES = new Set(["completed", "completed_with_warnings", "failed", "expired"]);

function isResendable(job) {
  return RESENDABLE_STATUSES.has(job?.status) && Boolean(getEmailSummary(job));
}

// Returns a delivery summary or null when no email was expected for this job.
function getEmailSummary(job) {
  const recipients = Array.isArray(job?.emailRecipients) ? job.emailRecipients : [];
  const results = getEmailResults(job);
  const total = recipients.length || results.length;
  if (total === 0) {
    return null;
  }
  const sent = results.filter(isRecipientSent).length;
  const failed = results.filter(isRecipientFailed).length;
  let state = "pending";
  if (results.length > 0) {
    if (failed === 0 && sent >= total) {
      state = "sent";
    } else if (sent > 0) {
      state = "partial";
    } else {
      state = "failed";
    }
  }
  return { total, sent, failed, state };
}

function getEmailBadgeClassName(state) {
  if (state === "sent") return "email-badge email-badge-sent";
  if (state === "partial") return "email-badge email-badge-partial";
  if (state === "failed") return "email-badge email-badge-failed";
  return "email-badge email-badge-pending";
}

function formatSentAt(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleString("fr-FR");
}

const AdminExecutionsPage = () => {
  const { instance, accounts } = useMsal();
  const { t } = useI18n();
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [templateFilter, setTemplateFilter] = useState("all");
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [stoppingJobId, setStoppingJobId] = useState(null);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const [deletingJobId, setDeletingJobId] = useState(null);
  const [resendingJobId, setResendingJobId] = useState(null);
  const [expandedJobs, setExpandedJobs] = useState({});
  const [serverCapacity, setServerCapacity] = useState(null);
  const [isCapacityLoading, setIsCapacityLoading] = useState(true);
  const recentlyDeletedRef = useRef(new Set());

  const getApiToken = useCallback(async () => {
    const accessToken = await instance.acquireTokenSilent({
      scopes: ["api://c247d402-5b9d-40f2-b7e6-b15a47c56f22/Api.access"],
      account: accounts[0],
    });
    return accessToken.accessToken;
  }, [accounts, instance]);

  const loadJobs = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setIsLoading(true);
        setIsCapacityLoading(true);
      }
      setError(null);
      try {
        const token = await getApiToken();
        const [data, capacity] = await Promise.all([
          listAdminReportJobs({ token, limit: 500 }),
          getReportCapacity({ token }),
        ]);
        const deleted = recentlyDeletedRef.current;
        const freshJobs = (Array.isArray(data.jobs) ? data.jobs : []).filter(
          (job) => !deleted.has(job.jobId)
        );
        setJobs(freshJobs);
        setServerCapacity(capacity || null);
      } catch (e) {
        setError(e.message || "Failed to load admin jobs");
        setServerCapacity(null);
      } finally {
        if (!silent) {
          setIsLoading(false);
          setIsCapacityLoading(false);
        }
      }
    },
    [getApiToken]
  );

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      loadJobs({ silent: true });
    }, POLL_MS);
    return () => window.clearInterval(interval);
  }, [loadJobs]);

  const statusOptions = useMemo(() => getUniqueValues(jobs, "status"), [jobs]);
  const clientOptions = useMemo(() => getUniqueValues(jobs, "clientName"), [jobs]);
  const templateOptions = useMemo(() => getUniqueValues(jobs, "templateName"), [jobs]);

  const filteredAndSortedJobs = useMemo(() => {
    const search = userSearch.trim().toLowerCase();
    const filtered = jobs.filter((job) => {
      const matchesUser = !search || String(job.ownerEmail || "").toLowerCase().includes(search);
      const matchesStatus = statusFilter === "all" || job.status === statusFilter;
      const matchesClient = clientFilter === "all" || job.clientName === clientFilter;
      const matchesTemplate = templateFilter === "all" || job.templateName === templateFilter;
      return matchesUser && matchesStatus && matchesClient && matchesTemplate;
    });

    const sorted = [...filtered].sort((a, b) => {
      let left = a[sortKey];
      let right = b[sortKey];

      if (sortKey === "createdAt" || sortKey === "completedAt") {
        left = left ? new Date(left).getTime() : 0;
        right = right ? new Date(right).getTime() : 0;
      } else {
        left = String(left || "").toLowerCase();
        right = String(right || "").toLowerCase();
      }

      if (left < right) {
        return sortDirection === "asc" ? -1 : 1;
      }
      if (left > right) {
        return sortDirection === "asc" ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [jobs, userSearch, statusFilter, clientFilter, templateFilter, sortKey, sortDirection]);

  const handleSort = useCallback((key) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection("asc");
  }, [sortKey]);

  const toggleExpandedJob = useCallback((jobId) => {
    setExpandedJobs((prev) => ({ ...prev, [jobId]: !prev[jobId] }));
  }, []);

  const handleStopJob = useCallback(
    async (jobId) => {
      if (!jobId || stoppingJobId) {
        return;
      }
      setStoppingJobId(jobId);
      setError(null);
      try {
        const token = await getApiToken();
        await stopAdminReportJob(jobId, { token });
        await loadJobs({ silent: true });
      } catch (e) {
        setError(e.message || "Failed to stop job");
      } finally {
        setStoppingJobId(null);
      }
    },
    [getApiToken, loadJobs, stoppingJobId]
  );

  const handleViewJob = useCallback(
    async (job) => {
      if (!job?.jobId || downloadingJobId) {
        return;
      }
      setDownloadingJobId(job.jobId);
      setError(null);
      try {
        const token = await getApiToken();
        const { blob, fileName } = await downloadAdminReport(job.jobId, { token });
        const fallback = `${job.clientName || "client"}_${job.templateName || "report"}.pdf`;
        triggerBrowserDownload(blob, fileName || fallback);
      } catch (e) {
        setError(e.message || "Failed to download report");
      } finally {
        setDownloadingJobId(null);
      }
    },
    [downloadingJobId, getApiToken]
  );

  const handleResendEmails = useCallback(
    async (job) => {
      if (!job?.jobId || resendingJobId) {
        return;
      }
      // Target only failed/bounced recipients when there are any; otherwise
      // resend to everyone (e.g. a recipient claims they never received it).
      const failed = getFailedRecipients(job);
      const targetLabel = failed.length > 0 ? failed.join(", ") : t("resend_all_recipients");
      const failedJobNote = job.status === "failed" || job.status === "expired"
        ? `\n\n${t("resend_failed_job_note")}`
        : "";
      if (!window.confirm(`${t("resend_confirm")}\n\n${targetLabel}${failedJobNote}`)) {
        return;
      }
      setResendingJobId(job.jobId);
      setError(null);
      try {
        const token = await getApiToken();
        await resendAdminReportEmails(job.jobId, {
          token,
          recipients: failed.length > 0 ? failed : undefined,
        });
        await loadJobs({ silent: true });
      } catch (e) {
        setError(e.message || "Failed to resend report emails");
      } finally {
        setResendingJobId(null);
      }
    },
    [getApiToken, loadJobs, resendingJobId, t]
  );

  const handleDeleteJob = useCallback(
    async (job) => {
      const jobId = job?.jobId;
      if (!jobId || deletingJobId) {
        return;
      }
      setDeletingJobId(jobId);
      setError(null);
      recentlyDeletedRef.current.add(jobId);
      try {
        const token = await getApiToken();
        if (job.status === "queued" || job.status === "running") {
          await stopAdminReportJob(jobId, { token });
        }
        await deleteAdminReportJob(jobId, { token });
        await new Promise((resolve) => window.setTimeout(resolve, 700));
        await loadJobs({ silent: true });
      } catch (e) {
        setError(e.message || "Failed to delete job");
        recentlyDeletedRef.current.delete(jobId);
      } finally {
        setDeletingJobId(null);
        window.setTimeout(() => {
          recentlyDeletedRef.current.delete(jobId);
        }, 15_000);
      }
    },
    [deletingJobId, getApiToken, loadJobs]
  );

  const capacityMax = Number(serverCapacity?.maxConcurrentExecutions || 0);
  const capacityRunning = Number(serverCapacity?.runningJobs || 0);
  const capacityRatio = capacityMax > 0 ? Math.min(capacityRunning / capacityMax, 1) : 0;
  const capacityPercent = Math.round(capacityRatio * 100);
  const capacityRingStyle = {
    background: `conic-gradient(#3db29d ${capacityPercent}%, #dbe4ef ${capacityPercent}% 100%)`,
  };
  const capacityTitle = isCapacityLoading
    ? t("server_capacity_loading")
    : (serverCapacity ? `${capacityRunning}/${capacityMax} ${t("server_executions_in_progress")}` : t("server_capacity_unavailable"));

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-header-actions">
          <div className="admin-capacity-indicator" title={capacityTitle}>
            <div className="admin-capacity-ring" style={capacityRingStyle}>
              <div className="admin-capacity-ring-inner">
                {isCapacityLoading ? "..." : `${capacityPercent}%`}
              </div>
            </div>
            <div className="admin-capacity-text">
              <strong>{capacityTitle}</strong>
              {serverCapacity && (
                <span>
                  {t("running")}: {serverCapacity.runningJobs} | {t("queued")}: {serverCapacity.queuedJobs} | {t("max_capacity")}: {serverCapacity.maxConcurrentExecutions}
                </span>
              )}
            </div>
          </div>
          <button className="button-generate" onClick={() => loadJobs()} disabled={isLoading}>
            {isLoading ? (
              <span className="btn-loading-content">
                <span className="button-spinner" aria-hidden="true" />
                {t("loading")}
              </span>
            ) : (
              t("refresh")
            )}
          </button>
        </div>
      </div>

      {error && <p className="admin-error">{error}</p>}

      <div className="admin-filters">
        <div className="admin-filters-row">
          <input
            className="admin-search"
            type="text"
            placeholder={t("search_user")}
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">{t("all_statuses")}</option>
            {statusOptions.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
          <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
            <option value="all">{t("all_clients")}</option>
            {clientOptions.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
          <select value={templateFilter} onChange={(e) => setTemplateFilter(e.target.value)}>
            <option value="all">{t("all_templates")}</option>
            {templateOptions.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("ownerEmail")}>{t("user")}</th>
              <th onClick={() => handleSort("clientName")}>{t("client")}</th>
              <th onClick={() => handleSort("templateName")}>Template</th>
              <th className="admin-th-dates">{t("dates")}</th>
              <th onClick={() => handleSort("status")}>{t("status")}</th>
              <th onClick={() => handleSort("progressPercent")}>{t("progress")}</th>
              <th className="admin-th-expand" />
              <th>{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && filteredAndSortedJobs.length === 0 && (
              <tr>
                <td colSpan={8}>{t("no_admin_execution")}</td>
              </tr>
            )}
            {filteredAndSortedJobs.map((job) => (
              <React.Fragment key={job.jobId}>
                <tr
                  className={`admin-row-clickable ${deletingJobId === job.jobId ? "admin-row-deleting" : ""} ${expandedJobs[job.jobId] ? "admin-row-expanded" : ""} ${job.jobSource === "coordinator" ? "admin-row-coordinator" : ""}`}
                  onClick={() => toggleExpandedJob(job.jobId)}
                >
                  <td>{job.ownerEmail || "-"}</td>
                  <td>
                    <div className="admin-client-cell">
                      <ClientThumb clientName={job.clientName} getApiToken={getApiToken} size={18} />
                      {job.clientName || "-"}
                    </div>
                  </td>
                  <td>{job.templateName || "-"}</td>
                  <td className="admin-dates-cell">{formatDateRange(job.startDate, job.endDate)}</td>
                  <td>
                    <div className="admin-status-cell">
                      <span className={getStatusClassName(job.status)}>
                        {job.status || "-"}
                      </span>
                      {(() => {
                        const emailSummary = getEmailSummary(job);
                        if (!emailSummary) {
                          return null;
                        }
                        const icon = emailSummary.state === "sent" ? "\u2709" : emailSummary.state === "failed" ? "\u2716" : "\u26A0";
                        return (
                          <span
                            className={getEmailBadgeClassName(emailSummary.state)}
                            title={`${t("email_delivery")}: ${emailSummary.sent}/${emailSummary.total}`}
                          >
                            {icon} {emailSummary.sent}/{emailSummary.total}
                          </span>
                        );
                      })()}
                    </div>
                  </td>
                  <td>{Number.isFinite(Number(job.progressPercent)) ? `${job.progressPercent}%` : "-"}</td>
                  <td className="admin-expand-cell">
                    <span className={`admin-expand-icon ${expandedJobs[job.jobId] ? "admin-expand-icon-open" : ""}`}>
                      &#9662;
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {(job.status === "queued" || job.status === "running") ? (
                      <div className="admin-action-group">
                        <button
                          className="admin-stop-button"
                          onClick={() => handleStopJob(job.jobId)}
                          disabled={Boolean(stoppingJobId || downloadingJobId || deletingJobId)}
                        >
                          {stoppingJobId === job.jobId ? t("stop_in_progress") : t("stop")}
                        </button>
                        <button
                          className="admin-delete-button"
                          onClick={() => handleDeleteJob(job)}
                          disabled={Boolean(stoppingJobId || downloadingJobId || deletingJobId)}
                        >
                          {deletingJobId === job.jobId ? t("deleting") : t("delete")}
                        </button>
                      </div>
                    ) : (
                      <div className="admin-action-group">
                        {(job.status === "completed" || job.status === "completed_with_warnings") && (
                          <button
                            className="admin-view-button"
                            onClick={() => handleViewJob(job)}
                            disabled={Boolean(stoppingJobId || downloadingJobId || deletingJobId)}
                          >
                            {downloadingJobId === job.jobId ? t("open") : t("view")}
                          </button>
                        )}
                        {isResendable(job) && (
                          <button
                            className="admin-resend-button"
                            onClick={() => handleResendEmails(job)}
                            disabled={Boolean(stoppingJobId || downloadingJobId || deletingJobId || resendingJobId)}
                          >
                            {resendingJobId === job.jobId ? t("resend_in_progress") : t("resend")}
                          </button>
                        )}
                        <button
                          className="admin-delete-button"
                          onClick={() => handleDeleteJob(job)}
                          disabled={Boolean(stoppingJobId || downloadingJobId || deletingJobId)}
                        >
                          {deletingJobId === job.jobId ? t("deleting") : t("delete")}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                {expandedJobs[job.jobId] && (
                  <tr className={`admin-details-row ${deletingJobId === job.jobId ? "admin-row-deleting" : ""} ${job.jobSource === "coordinator" ? "admin-row-coordinator" : ""}`}>
                    <td colSpan={8}>
                      <div className="admin-details-grid">
                        <span><strong>Job ID:</strong> <span className="admin-detail-mono">{job.jobId}</span></span>
                        <span><strong>{t("job_source")}:</strong> {job.jobSource || "-"}</span>
                        <span><strong>{t("start_period")}:</strong> {formatIsoDate(job.startDate)}</span>
                        <span><strong>{t("end_period")}:</strong> {formatIsoDate(job.endDate)}</span>
                        <span><strong>{t("created_at")}:</strong> {formatIsoDate(job.createdAt)}</span>
                        <span><strong>{t("completed_at")}:</strong> {formatIsoDate(job.completedAt)}</span>
                      </div>
                      {getJobWarningMessage(job) && (
                        <div className="admin-warning-box">
                          <strong>{t("warnings")}:</strong> {getJobWarningMessage(job)}
                        </div>
                      )}
                      {job.error?.code && (
                        <div className="admin-detail-line">
                          <strong>{t("error_code")}:</strong> {job.error.code}
                        </div>
                      )}
                      {job.error?.message && (
                        <div className="admin-detail-line">
                          <strong>{t("error_message")}:</strong> {job.error.message}
                        </div>
                      )}
                      {job.period && (
                        <div className="admin-detail-line">
                          <strong>{t("period")}:</strong> {job.period}
                        </div>
                      )}
                      {(() => {
                        const results = getEmailResults(job);
                        const recipients = Array.isArray(job.emailRecipients) ? job.emailRecipients : [];
                        if (results.length === 0 && recipients.length === 0) {
                          return null;
                        }
                        // Prefer per-recipient results; fall back to the plain recipient list.
                        const rows = results.length > 0
                          ? results
                          : recipients.map((recipient) => ({ recipient, status: "pending" }));
                        return (
                          <div className="admin-detail-line">
                            <strong>{t("email_status_detail")}:</strong>
                            <ul className="admin-email-list">
                              {rows.map((row, index) => {
                                const bounced = isRecipientBounced(row);
                                const delivered = isRecipientDelivered(row);
                                const sent = isRecipientSent(row);
                                const failed = isRecipientFailed(row);
                                const label = bounced
                                  ? t("email_bounced")
                                  : delivered
                                    ? t("email_delivered")
                                    : sent
                                      ? t("email_sent")
                                      : failed
                                        ? t("email_failed")
                                        : "-";
                                const cls = sent ? "email-status-ok" : failed ? "email-status-ko" : "";
                                const sentAt = formatSentAt(row.sentAt);
                                return (
                                  <li key={`${row.recipient || "recipient"}-${index}`} className="admin-email-list-item">
                                    <span className="admin-email-recipient">{row.recipient || "-"}</span>
                                    <span className={`admin-email-status ${cls}`}>{label}</span>
                                    {sent && sentAt && (
                                      <span className="admin-email-sent-at">{t("sent_at")} {sentAt}</span>
                                    )}
                                    {failed && row.error && (
                                      <span className="admin-email-error">{row.error}</span>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminExecutionsPage;
